import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, Wallet, Hash, Trophy, Share2, Printer } from 'lucide-react';

const steps = [
  {
    n: '01',
    title: 'Open the app',
    body: 'Tap our bot in Telegram and hit the menu button. No sign-up form.',
  },
  {
    n: '02',
    title: 'Add some XRP',
    body: 'Tap Deposit, copy the address, send any amount from your Xaman wallet. Balance updates in ~30s.',
  },
  {
    n: '03',
    title: 'Pick a room & a number (0–9)',
    body: 'Tap any open slot. Each room has 10 of them. Your XRP goes in. Now you wait.',
  },
  {
    n: '04',
    title: 'Watch the draw',
    body: 'Twice daily, the Illinois Pick 3 Fireball draws a number. Match your slot? Whole pool is yours.',
  },
];

const glossary = [
  ['XRP', 'A type of crypto money — like dollars, but on the internet.'],
  ['Slot 0–9', 'Pick a number from 0 to 9. Each room has 10 of them.'],
  ['Pool Room', 'Regular game. Winner takes the cash pool.'],
  ['Prize Room', 'Win a real thing — Pokémon card, NFT, watch — instead of cash.'],
  ['BIN', 'Buy It Now. Skip the game, pay full price, prize is yours.'],
  ['Make Offer', "Tell the seller 'I'll pay less.' They accept, counter, or pass."],
  ['Auction', 'Timed bidding war. Highest bid when the clock ends wins.'],
  ['Host', 'You list a prize, others play for it. You keep the pool of slot sales (minus the 5.89% platform fee).'],
  ['Escrow', 'Safety lock-box. Your XRP is held until the prize is in your hands.'],
  ['Draw', 'Midday & evening. When the lottery picks the winner.'],
];

export default function Guide() {
  const navigate = useNavigate();

  const handleShare = async () => {
    const shareData = {
      title: 'Vitamin C Gainz — How It Works',
      text: 'Pick a number. Win the pool. The whole game in 4 steps.',
      url: typeof window !== 'undefined' ? window.location.href : '',
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareData.url);
      }
    } catch {
      // user cancelled or share unavailable — silent
    }
  };

  const handlePrint = () => { window.print(); };

  return (
    <div data-testid="guide-page" className="min-h-screen bg-[#0B0B14] text-white print:bg-white print:text-black">
      {/* Sticky header — hidden on print */}
      <div className="sticky top-0 z-10 bg-[#0B0B14]/85 backdrop-blur-md border-b border-white/5 print:hidden">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            data-testid="guide-back-btn"
            onClick={() => navigate(-1)}
            className="text-slate-300 hover:text-white inline-flex items-center gap-1.5 text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex gap-1.5">
            <button
              data-testid="guide-share-btn"
              onClick={handleShare}
              className="inline-flex items-center gap-1.5 text-xs bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-3 py-1.5"
            >
              <Share2 className="w-3 h-3" /> Share
            </button>
            <button
              data-testid="guide-print-btn"
              onClick={handlePrint}
              className="inline-flex items-center gap-1.5 text-xs bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-3 py-1.5"
            >
              <Printer className="w-3 h-3" /> Print
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-5 md:px-8 py-8 md:py-10 print:py-4">
        {/* HERO */}
        <header className="relative">
          <div className="absolute -top-10 -left-10 w-64 h-64 bg-orange-500/20 blur-3xl rounded-full pointer-events-none print:hidden" />
          <div className="absolute -top-16 right-0 w-72 h-72 bg-amber-300/10 blur-3xl rounded-full pointer-events-none print:hidden" />

          <p className="text-[10px] font-bold tracking-[0.25em] text-orange-400 uppercase mb-2 print:text-orange-600">
            How it works · 60-second guide
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
            <span className="bg-gradient-to-r from-orange-400 via-amber-300 to-yellow-200 bg-clip-text text-transparent print:text-orange-600">Pick a number.</span>
            <br />
            <span className="text-white print:text-black">Win the pool.</span>
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-300 max-w-xl print:text-slate-700">
            Vitamin C Gainz is a game where you pick a slot, and if your number is drawn, you win the whole prize pool — paid out in real crypto.
          </p>

          <div className="mt-6 flex flex-wrap gap-2 text-[11px]">
            <span className="inline-flex items-center gap-1.5 bg-orange-500/10 border border-orange-500/30 text-orange-300 rounded-full px-3 py-1 print:bg-orange-100 print:text-orange-700 print:border-orange-300">
              <Sparkles className="w-3 h-3" /> Real XRP payouts
            </span>
            <span className="inline-flex items-center gap-1.5 bg-amber-300/10 border border-amber-300/30 text-amber-200 rounded-full px-3 py-1 print:bg-amber-100 print:text-amber-700 print:border-amber-300">
              <Trophy className="w-3 h-3" /> Real prizes
            </span>
            <span className="inline-flex items-center gap-1.5 bg-purple-500/10 border border-purple-500/30 text-purple-300 rounded-full px-3 py-1 print:bg-purple-100 print:text-purple-700 print:border-purple-300">
              <Hash className="w-3 h-3" /> Provably fair draw
            </span>
          </div>
        </header>

        {/* STEPS */}
        <section className="mt-10 print:mt-8">
          <h2 className="text-[11px] font-bold tracking-[0.2em] text-slate-500 uppercase mb-4 print:text-slate-600">
            Get started in 4 steps
          </h2>
          <ol className="grid gap-3 sm:grid-cols-2">
            {steps.map((s) => (
              <li
                key={s.n}
                data-testid={`guide-step-${s.n}`}
                className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] p-4 sm:p-5 print:bg-white print:border-slate-200 print:break-inside-avoid"
              >
                <div className="text-5xl font-black text-orange-400/20 absolute top-2 right-3 leading-none print:text-orange-300/40">
                  {s.n}
                </div>
                <p className="text-[10px] font-bold tracking-widest text-orange-400 mb-1 print:text-orange-600">STEP {s.n}</p>
                <h3 className="text-lg font-bold text-white print:text-black">{s.title}</h3>
                <p className="mt-1.5 text-sm text-slate-400 leading-relaxed print:text-slate-700">{s.body}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* THE WHOLE GAME */}
        <section className="mt-8 print:mt-6">
          <div className="rounded-2xl border border-orange-500/20 bg-gradient-to-br from-orange-500/10 via-amber-300/5 to-transparent p-5 sm:p-6 print:bg-orange-50 print:border-orange-200 print:break-inside-avoid">
            <p className="text-[10px] font-bold tracking-[0.25em] text-orange-400 uppercase mb-1 print:text-orange-600">The whole game</p>
            <p className="text-xl sm:text-2xl font-bold text-white print:text-black">
              Pick 0–9. Wait for the lottery draw. Match = entire pool is yours.
            </p>
            <p className="mt-2 text-sm text-slate-400 print:text-slate-700">
              Backed by the Illinois Pick 3 Fireball — we can't rig it. Two draws every day: midday and evening.
            </p>
          </div>
        </section>

        {/* GLOSSARY */}
        <section className="mt-10 print:mt-8">
          <h2 className="text-[11px] font-bold tracking-[0.2em] text-slate-500 uppercase mb-4 print:text-slate-600">
            Words you might see
          </h2>
          <dl className="divide-y divide-white/5 rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden print:divide-slate-200 print:border-slate-200 print:bg-white">
            {glossary.map(([term, def]) => (
              <div key={term} className="grid grid-cols-[120px_1fr] sm:grid-cols-[160px_1fr] gap-3 px-4 py-3 print:break-inside-avoid">
                <dt className="text-sm font-bold text-orange-300 print:text-orange-700">{term}</dt>
                <dd className="text-sm text-slate-300 print:text-slate-700">{def}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* BEGINNER TIPS */}
        <section className="mt-10 print:mt-8 print:break-inside-avoid">
          <h2 className="text-[11px] font-bold tracking-[0.2em] text-slate-500 uppercase mb-4 print:text-slate-600">
            Beginner tips
          </h2>
          <ul className="space-y-2 text-sm">
            <li className="flex gap-3 text-slate-300 print:text-slate-700">
              <span className="text-orange-400 font-bold print:text-orange-600">→</span>
              <span><strong className="text-white print:text-black">Start small.</strong> Quick Flip room is 5 XRP. Win it, take home 50.</span>
            </li>
            <li className="flex gap-3 text-slate-300 print:text-slate-700">
              <span className="text-orange-400 font-bold print:text-orange-600">→</span>
              <span><strong className="text-white print:text-black">Treat it like a movie ticket.</strong> It's a game of chance — fun, not an investment.</span>
            </li>
            <li className="flex gap-3 text-slate-300 print:text-slate-700">
              <span className="text-orange-400 font-bold print:text-orange-600">→</span>
              <span><strong className="text-white print:text-black">Want to host?</strong> List your own prize — you keep the full pool of slot sales, minus the 5.89% platform fee.</span>
            </li>
          </ul>
        </section>

        {/* FOOTER */}
        <footer className="mt-12 mb-6 pt-6 border-t border-white/5 text-center print:border-slate-200">
          <p className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-orange-400 to-amber-300 bg-clip-text text-transparent print:text-orange-600">
            Stack the gainz. <Wallet className="w-5 h-5 inline-block opacity-70" />
          </p>
          <p className="mt-1 text-xs text-slate-500 print:text-slate-600">vitamincgainz.com</p>
        </footer>
      </main>
    </div>
  );
}
